import { useState, useEffect, useCallback } from 'react';
import permissionService, {
    UserPermissions,
    ModulePermission,
    ContextPermissions,
    PermissionHistoryEntry,
    AvailableModule,
    AvailableScope
} from '../services/PermissionService';

interface UseModulePermissionsReturn {
    // Data
    userPermissions: UserPermissions | null;
    availableModules: AvailableModule[];
    availableScopes: AvailableScope[];
    permissionHistory: PermissionHistoryEntry[];
    
    // Loading states
    loading: boolean;
    saving: boolean;
    
    // Error state
    error: string | null;
    
    // Actions
    loadUserPermissions: (userId: string) => Promise<void>;
    loadAvailableModules: () => Promise<void>;
    loadPermissionHistory: (userId: string) => Promise<void>;
    grantModuleAccess: (userId: string, moduleData: ModulePermission, reason?: string) => Promise<void>;
    revokeModuleAccess: (userId: string, module: string, reason?: string) => Promise<void>;
    updateContextPermissions: (userId: string, contextData: ContextPermissions, reason?: string) => Promise<void>;
    checkPermission: (userId: string, resource: string, action: string, context?: any) => Promise<boolean>;
    clearError: () => void;
}

export const useModulePermissions = (): UseModulePermissionsReturn => {
    const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
    const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
    const [availableScopes, setAvailableScopes] = useState<AvailableScope[]>([]);
    const [permissionHistory, setPermissionHistory] = useState<PermissionHistoryEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const loadUserPermissions = useCallback(async (userId: string) => {
        setLoading(true);
        setError(null);
        try {
            const permissions = await permissionService.getUserPermissions(userId);
            setUserPermissions(permissions);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to load user permissions');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAvailableModules = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await permissionService.getAvailableModules();
            setAvailableModules(data.modules);
            setAvailableScopes(data.scopes);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to load available modules');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPermissionHistory = useCallback(async (userId: string) => {
        setLoading(true);
        setError(null);
        try {
            const history = await permissionService.getPermissionHistory(userId);
            setPermissionHistory(history);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to load permission history');
        } finally {
            setLoading(false);
        }
    }, []);

    const grantModuleAccess = useCallback(async (
        userId: string,
        moduleData: ModulePermission,
        reason?: string
    ) => {
        setSaving(true);
        setError(null);
        try {
            await permissionService.grantModuleAccess(userId, moduleData, reason);
            // Reload user permissions to get updated data
            await loadUserPermissions(userId);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to grant module access');
            throw err; // Re-throw so the calling component can handle it
        } finally {
            setSaving(false);
        }
    }, [loadUserPermissions]);

    const revokeModuleAccess = useCallback(async (
        userId: string,
        module: string,
        reason?: string
    ) => {
        setSaving(true);
        setError(null);
        try {
            await permissionService.revokeModuleAccess(userId, module, reason);
            // Reload user permissions to get updated data
            await loadUserPermissions(userId);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to revoke module access');
            throw err;
        } finally {
            setSaving(false);
        }
    }, [loadUserPermissions]);

    const updateContextPermissions = useCallback(async (
        userId: string,
        contextData: ContextPermissions,
        reason?: string
    ) => {
        setSaving(true);
        setError(null);
        try {
            await permissionService.updateContextPermissions(userId, contextData, reason);
            // Reload user permissions to get updated data
            await loadUserPermissions(userId);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to update context permissions');
            throw err;
        } finally {
            setSaving(false);
        }
    }, [loadUserPermissions]);

    const checkPermission = useCallback(async (
        userId: string,
        resource: string,
        action: string,
        context?: any
    ): Promise<boolean> => {
        try {
            return await permissionService.checkPermission(userId, resource, action, context);
        } catch (err: any) {
            console.error('Error checking permission:', err);
            return false;
        }
    }, []);

    // Load available modules on mount
    useEffect(() => {
        loadAvailableModules();
    }, [loadAvailableModules]);

    return {
        // Data
        userPermissions,
        availableModules,
        availableScopes,
        permissionHistory,
        
        // Loading states
        loading,
        saving,
        
        // Error state
        error,
        
        // Actions
        loadUserPermissions,
        loadAvailableModules,
        loadPermissionHistory,
        grantModuleAccess,
        revokeModuleAccess,
        updateContextPermissions,
        checkPermission,
        clearError
    };
};

export default useModulePermissions;